import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Copy, Loader2, RefreshCw } from 'lucide-react';
import type { InterviewLink } from '../../services/interviewLinksApi';
import { ensureShareLink, isFirstInterviewStage, isScreeningLink } from '../../services/linkedinShareApi';

/**
 * "Copy LinkedIn link" and "Replace link" on a First Interview link (lib/linkedinShare.js).
 *
 * Copy is the common action and costs one click: it copies the URL the list already carries, and
 * mints one first for a link saved before the feature existed. "Replace link" is the rare one — it
 * REPLACES the URL, so every InMail already sent stops working — which is why it is small, ghost,
 * and asks first.
 */
export function LinkedInLinkActions({ link }: { link: InterviewLink }) {
  // The URL as of the last mint here; the list's copy goes stale after a regenerate until refetch.
  const [url, setUrl] = useState<string | null>(link.shareUrl ?? null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // First Interview links, and the TA managers' screening links (Oct 24 2026), have a LinkedIn URL.
  if ((!isFirstInterviewStage(link.stageId) && !isScreeningLink(link)) || !link.active) return null;

  async function copy(text: string, message: string) {
    try {
      await navigator.clipboard.writeText(text);
      showToast({ status: 'success', description: message });
    } catch {
      // A browser that refuses the clipboard still gets the URL, to copy by hand.
      showToast({ status: 'info', description: text });
    }
  }

  async function run(regenerate: boolean) {
    setBusy(true);
    try {
      const next = !regenerate && url ? url : await ensureShareLink(link.id, { regenerate });
      setUrl(next);
      await copy(
        next,
        regenerate
          ? 'New LinkedIn link copied. The old one no longer works.'
          : 'LinkedIn link copied — paste it into your InMail.'
      );
    } catch (err) {
      showToast({
        status: 'error',
        description: err instanceof Error ? err.message : 'Could not get the LinkedIn link',
      });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => void run(false)}
        disabled={busy}
        title="Copy the reusable booking link for LinkedIn InMails"
      >
        {busy ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Copy className="mr-1.5 h-3.5 w-3.5" />
        )}
        Copy LinkedIn link
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setConfirming(true)}
        disabled={busy}
        title="Replace the LinkedIn link — the old one stops working"
        // The visible label is short; the accessible name says WHICH link it replaces.
        aria-label="Replace the LinkedIn link"
      >
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
        Replace link
      </Button>
      {confirming ? (
        <Modal open onOpenChange={(open: boolean) => !open && setConfirming(false)}>
          <ModalContent className="!h-max !w-[min(100vw-2rem,28rem)] gap-4 rounded p-6">
            <ModalHeader>
              <ModalTitle>Replace the LinkedIn link?</ModalTitle>
            </ModalHeader>
            <p className="text-sm text-gray-700">
              The old link stops working for anyone who hasn’t booked yet, including people you have
              already sent it to. Interviews already booked through it are not affected.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
                Keep the current link
              </Button>
              <Button onClick={() => void run(true)} disabled={busy}>
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Replace and copy
              </Button>
            </div>
          </ModalContent>
        </Modal>
      ) : null}
    </>
  );
}
